import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supplierApplicationService, supplierDocumentService } from '../../services/supplierApplicationService';
import { format } from 'date-fns';
import { useLanguage } from '../../contexts/LanguageContext';

const SupplierApplicationStatus = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [activeTab, setActiveTab] = useState('timeline');
  const [emailPreferences, setEmailPreferences] = useState({
    statusUpdates: true,
    documentRequests: true,
    adminMessages: true
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadApplicationData();
  }, []);

  const loadApplicationData = async () => {
    try {
      setLoading(true);
      const applications = await supplierApplicationService?.getMyApplications();
      if (applications?.length > 0) {
        const latestApp = applications?.[0];
        setApplication(latestApp);
        const docs = await supplierDocumentService?.getDocumentsByApplication(latestApp?.id);
        setDocuments(docs || []);
      }
    } catch (error) {
      console.error('Failed to load application:', error);
    } finally {
      setLoading(false);
    }
  };

  const content = {
    en: {
      title: 'Application Status',
      subtitle: 'Track your supplier verification and approval process',
      timeline: 'Timeline',
      documents: 'Documents',
      communication: 'Communication',
      settings: 'Settings',
      noApplication: 'No application found',
      noApplicationDesc: 'You have not submitted a supplier application yet.',
      startApplication: 'Start Application',
      stage1: 'Registration',
      stage1Desc: 'Basic information submitted',
      stage2: 'Document Review',
      stage2Desc: 'Credentials verification in progress',
      stage3: 'Credential Verification',
      stage3Desc: 'Admin reviewing certifications',
      stage4: 'Admin Evaluation',
      stage4Desc: 'Final business assessment',
      stage5: 'Approval',
      stage5Desc: 'Application decision',
      completed: 'Completed',
      current: 'Current',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      underReview: 'Under Review',
      documentsPending: 'Documents Pending',
      businessLicense: 'Business License',
      exportPermit: 'Export Permit',
      organicCert: 'Organic Certification',
      fairTradeCert: 'Fair Trade Certification',
      specialtyCert: 'Specialty Certification',
      taxRegistration: 'Tax Registration',
      bankVerification: 'Bank Verification',
      qualityReport: 'Quality Test Report',
      adminFeedback: 'Admin Feedback',
      uploadMissing: 'Upload Missing',
      reupload: 'Re-upload',
      viewDocument: 'View',
      estimatedTimeline: 'Estimated Timeline',
      expectedCompletion: 'Expected Completion',
      processingTime: 'Processing Time',
      days: 'days',
      actionItems: 'Action Items',
      noActions: 'No pending actions',
      requiredDocuments: 'Required Documents',
      correctionsNeeded: 'Corrections Needed',
      emailNotifications: 'Email Notifications',
      statusUpdates: 'Application status updates',
      documentRequests: 'Document verification requests',
      adminMessages: 'Admin messages and feedback',
      savePreferences: 'Save Preferences',
      profileCompletion: 'Profile Completion',
      applicationProgress: 'Application Progress',
      businessInfo: 'Business Information',
      businessName: 'Business Name',
      contactPerson: 'Contact Person',
      email: 'Email',
      phone: 'Phone',
      country: 'Country',
      supplierType: 'Supplier Type',
      yearsInOperation: 'Years in Operation',
      submittedOn: 'Submitted On',
      lastUpdated: 'Last Updated',
      applicationId: 'Application ID',
      noMessages: 'No messages yet',
      adminNotes: 'Admin Notes',
      rejectionReason: 'Rejection Reason'
    },
    zh: {
      title: '申请状态',
      subtitle: '跟踪您的供应商验证和批准流程',
      timeline: '时间线',
      documents: '文件',
      communication: '沟通',
      settings: '设置',
      noApplication: '未找到申请',
      noApplicationDesc: '您尚未提交供应商申请。',
      startApplication: '开始申请',
      stage1: '注册',
      stage1Desc: '基本信息已提交',
      stage2: '文件审核',
      stage2Desc: '凭证验证进行中',
      stage3: '凭证验证',
      stage3Desc: '管理员审核认证',
      stage4: '管理员评估',
      stage4Desc: '最终业务评估',
      stage5: '批准',
      stage5Desc: '申请决定',
      completed: '已完成',
      current: '当前',
      pending: '待处理',
      approved: '已批准',
      rejected: '已拒绝',
      underReview: '审核中',
      documentsPending: '文件待处理',
      businessLicense: '营业执照',
      exportPermit: '出口许可证',
      organicCert: '有机认证',
      fairTradeCert: '公平贸易认证',
      specialtyCert: '精品认证',
      taxRegistration: '税务登记',
      bankVerification: '银行验证',
      qualityReport: '质量检测报告',
      adminFeedback: '管理员反馈',
      uploadMissing: '上传缺失',
      reupload: '重新上传',
      viewDocument: '查看',
      estimatedTimeline: '预计时间表',
      expectedCompletion: '预计完成',
      processingTime: '处理时间',
      days: '天',
      actionItems: '待办事项',
      noActions: '无待处理操作',
      requiredDocuments: '必需文件',
      correctionsNeeded: '需要更正',
      emailNotifications: '电子邮件通知',
      statusUpdates: '申请状态更新',
      documentRequests: '文件验证请求',
      adminMessages: '管理员消息和反馈',
      savePreferences: '保存偏好',
      profileCompletion: '资料完成度',
      applicationProgress: '申请进度',
      businessInfo: '业务信息',
      businessName: '企业名称',
      contactPerson: '联系人',
      email: '电子邮件',
      phone: '电话',
      country: '国家',
      supplierType: '供应商类型',
      yearsInOperation: '运营年限',
      submittedOn: '提交于',
      lastUpdated: '最后更新',
      applicationId: '申请ID',
      noMessages: '暂无消息',
      adminNotes: '管理员备注',
      rejectionReason: '拒绝原因'
    }
  };

  const t = content?.[language];

  const getStatusStage = (status) => {
    const stages = {
      'pending': 1,
      'documents_pending': 2,
      'under_review': 3,
      'approved': 5,
      'rejected': 5
    };
    return stages?.[status] || 1;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'text-warning',
      'documents_pending': 'text-warning',
      'under_review': 'text-info',
      'approved': 'text-success',
      'rejected': 'text-danger'
    };
    return colors?.[status] || 'text-muted';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': t?.pending,
      'documents_pending': t?.documentsPending,
      'under_review': t?.underReview,
      'approved': t?.approved,
      'rejected': t?.rejected
    };
    return labels?.[status] || status;
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      'business_license': t?.businessLicense,
      'export_permit': t?.exportPermit,
      'organic_certification': t?.organicCert,
      'fair_trade_certification': t?.fairTradeCert,
      'specialty_certification': t?.specialtyCert,
      'tax_registration': t?.taxRegistration,
      'bank_verification': t?.bankVerification,
      'quality_test_report': t?.qualityReport
    };
    return labels?.[type] || type;
  };

  const getVerificationIcon = (status) => {
    if (status === 'approved') return { name: 'CheckCircle', color: 'var(--color-success)' };
    if (status === 'rejected') return { name: 'XCircle', color: 'var(--color-danger)' };
    return { name: 'Clock', color: 'var(--color-warning)' };
  };

  const calculateProgress = () => {
    if (!application) return 0;
    const currentStage = getStatusStage(application?.applicationStatus);
    return (currentStage / 5) * 100;
  };

  const getEstimatedDays = () => {
    if (!application) return 0;
    const status = application?.applicationStatus;
    if (status === 'pending') return 14;
    if (status === 'documents_pending') return 10;
    if (status === 'under_review') return 7;
    return 0;
  };

  const getActionItems = () => {
    const actions = [];
    if (application?.applicationStatus === 'documents_pending') {
      const requiredDocs = ['business_license', 'export_permit', 'organic_certification'];
      const uploadedTypes = documents?.map(d => d?.documentType);
      const missing = requiredDocs?.filter(type => !uploadedTypes?.includes(type));
      if (missing?.length > 0) {
        actions?.push({
          type: 'document',
          priority: 'high',
          message: `${t?.requiredDocuments}: ${missing?.map(getDocumentTypeLabel)?.join(', ')}`
        });
      }
    }
    const rejectedDocs = documents?.filter(d => d?.verificationStatus === 'rejected');
    if (rejectedDocs?.length > 0) {
      actions?.push({
        type: 'correction',
        priority: 'high',
        message: `${t?.correctionsNeeded}: ${rejectedDocs?.map(d => getDocumentTypeLabel(d?.documentType))?.join(', ')}`
      });
    }
    return actions;
  };

  const handleSavePreferences = () => {
    localStorage.setItem('emailPreferences', JSON.stringify(emailPreferences));
    alert(language === 'en' ? 'Preferences saved successfully' : '偏好设置保存成功');
  };

  const handleViewDocument = async (doc) => {
    try {
      const url = await supplierDocumentService?.getDocumentUrl(doc?.fileUrl);
      window.open(url, '_blank');
    } catch (error) {
      alert(error?.message || (language === 'en' ? 'Failed to load document' : '加载文件失败'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-[60px]">
          <div className="flex items-center justify-center h-[calc(100vh-60px)]">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted">{language === 'en' ? 'Loading...' : '加载中...'}</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-[60px]">
          <div className="max-w-2xl mx-auto px-5 py-16">
            <div className="bg-card rounded-lg shadow-card border border-border p-12 text-center">
              <div className="w-20 h-20 bg-muted/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="FileText" size={40} color="var(--color-muted)" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">
                {t?.noApplication}
              </h2>
              <p className="text-muted mb-8">{t?.noApplicationDesc}</p>
              <Button onClick={() => navigate('/supplier-registration')}>
                {t?.startApplication}
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-[60px]">
        <div className="max-w-7xl mx-auto px-5 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              {t?.title}
            </h1>
            <p className="text-muted">{t?.subtitle}</p>
          </div>

          {/* Status Overview Card */}
          <div className="bg-card rounded-lg shadow-card border border-border p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-muted mb-1">{t?.applicationId}</p>
                <p className="text-lg font-semibold text-foreground">{application?.id?.slice(0, 8)}</p>
              </div>
              <div>
                <p className="text-sm text-muted mb-1">{t?.submittedOn}</p>
                <p className="text-lg font-semibold text-foreground">
                  {application?.submittedAt ? format(new Date(application?.submittedAt), 'MMM dd, yyyy') : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted mb-1">{t?.lastUpdated}</p>
                <p className="text-lg font-semibold text-foreground">
                  {application?.updatedAt ? format(new Date(application?.updatedAt), 'MMM dd, yyyy') : '-'}
                </p>
              </div>
            </div>
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">{t?.applicationProgress}</span>
                <span className={`text-sm font-semibold ${getStatusColor(application?.applicationStatus)}`}>
                  {getStatusLabel(application?.applicationStatus)}
                </span>
              </div>
              <div className="w-full bg-muted/20 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-500"
                  style={{ width: `${calculateProgress()}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-card rounded-lg shadow-card border border-border mb-8">
            <div className="border-b border-border">
              <div className="flex overflow-x-auto">
                {['timeline', 'documents', 'communication', 'settings']?.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab
                        ? 'text-primary border-b-2 border-primary' : 'text-muted hover:text-foreground'
                      }`}
                  >
                    {t?.[tab]}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {/* Timeline Tab */}
              {activeTab === 'timeline' && (
                <div className="space-y-8">
                  {/* Progress Timeline */}
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-6">Verification Progress</h3>
                    <div className="space-y-6">
                      {[
                        { stage: 1, title: t?.stage1, desc: t?.stage1Desc, icon: 'FileText' },
                        { stage: 2, title: t?.stage2, desc: t?.stage2Desc, icon: 'Upload' },
                        { stage: 3, title: t?.stage3, desc: t?.stage3Desc, icon: 'Shield' },
                        { stage: 4, title: t?.stage4, desc: t?.stage4Desc, icon: 'UserCheck' },
                        { stage: 5, title: t?.stage5, desc: t?.stage5Desc, icon: 'CheckCircle' }
                      ]?.map((item, index) => {
                        const currentStage = getStatusStage(application?.applicationStatus);
                        const isCompleted = item?.stage < currentStage;
                        const isCurrent = item?.stage === currentStage;
                        const isPending = item?.stage > currentStage;

                        return (
                          <div key={index} className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <div
                                className={`w-12 h-12 rounded-full flex items-center justify-center ${isCompleted
                                    ? 'bg-success/10'
                                    : isCurrent
                                      ? 'bg-primary/10' : 'bg-muted/20'
                                  }`}
                              >
                                <Icon
                                  name={item?.icon}
                                  size={24}
                                  color={
                                    isCompleted
                                      ? 'var(--color-success)'
                                      : isCurrent
                                        ? 'var(--color-primary)'
                                        : 'var(--color-muted)'
                                  }
                                />
                              </div>
                              {index < 4 && (
                                <div
                                  className={`w-0.5 h-12 ${isCompleted ? 'bg-success' : 'bg-muted/20'
                                    }`}
                                ></div>
                              )}
                            </div>
                            <div className="flex-1 pb-6">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="text-base font-semibold text-foreground">{item?.title}</h4>
                                {isCompleted && (
                                  <span className="text-xs font-medium text-success">{t?.completed}</span>
                                )}
                                {isCurrent && (
                                  <span className="text-xs font-medium text-primary">{t?.current}</span>
                                )}
                                {isPending && (
                                  <span className="text-xs font-medium text-muted">{t?.pending}</span>
                                )}
                              </div>
                              <p className="text-sm text-muted">{item?.desc}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Estimated Timeline */}
                  <div className="bg-background rounded-lg p-6 border border-border">
                    <h3 className="text-lg font-semibold text-foreground mb-4">{t?.estimatedTimeline}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Icon name="Calendar" size={20} color="var(--color-primary)" />
                        </div>
                        <div>
                          <p className="text-sm text-muted">{t?.expectedCompletion}</p>
                          <p className="text-base font-semibold text-foreground">
                            {getEstimatedDays()} {t?.days}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-info/10 rounded-lg flex items-center justify-center">
                          <Icon name="Clock" size={20} color="var(--color-info)" />
                        </div>
                        <div>
                          <p className="text-sm text-muted">{t?.processingTime}</p>
                          <p className="text-base font-semibold text-foreground">7-14 {t?.days}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Items */}
                  <div className="bg-warning/5 rounded-lg p-6 border border-warning/20">
                    <h3 className="text-lg font-semibold text-foreground mb-4">{t?.actionItems}</h3>
                    {getActionItems()?.length > 0 ? (
                      <div className="space-y-3">
                        {getActionItems()?.map((action, index) => (
                          <div key={index} className="flex items-start gap-3">
                            <Icon name="AlertCircle" size={20} color="var(--color-warning)" />
                            <p className="text-sm text-foreground flex-1">{action?.message}</p>
                            <Button
                              size="sm"
                              onClick={() => navigate(`/supplier-registration?applicationId=${application?.id}&step=3`)}
                            >
                              {t?.uploadMissing}
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted">{t?.noActions}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Documents Tab */}
              {activeTab === 'documents' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Document Status</h3>
                  {documents?.length > 0 ? (
                    <div className="space-y-3">
                      {documents?.map((doc) => {
                        const icon = getVerificationIcon(doc?.verificationStatus);
                        return (
                          <div
                            key={doc?.id}
                            className="flex items-center justify-between p-4 bg-background rounded-lg border border-border"
                          >
                            <div className="flex items-center gap-4 flex-1">
                              <Icon name={icon?.name} size={24} color={icon?.color} />
                              <div className="flex-1">
                                <h4 className="text-sm font-semibold text-foreground">
                                  {getDocumentTypeLabel(doc?.documentType)}
                                </h4>
                                <p className="text-xs text-muted">{doc?.documentName}</p>
                                {doc?.rejectionReason && (
                                  <p className="text-xs text-danger mt-1">
                                    {t?.adminFeedback}: {doc?.rejectionReason}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewDocument(doc)}
                              >
                                {t?.viewDocument}
                              </Button>
                              {doc?.verificationStatus === 'rejected' && (
                                <Button
                                  size="sm"
                                  onClick={() => navigate(`/supplier-registration?applicationId=${application?.id}&step=3`)}
                                >
                                  {t?.reupload}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Icon name="FileText" size={48} color="var(--color-muted)" />
                      <p className="text-muted mt-4">No documents uploaded yet</p>
                      <Button
                        className="mt-4"
                        onClick={() => navigate(`/supplier-registration?applicationId=${application?.id}&step=3`)}
                      >
                        Upload Documents
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Communication Tab */}
              {activeTab === 'communication' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Admin Messages</h3>

                  {/* Admin Notes */}
                  {application?.adminNotes && (
                    <div className="bg-info/5 rounded-lg p-4 border border-info/20">
                      <div className="flex items-start gap-3">
                        <Icon name="MessageSquare" size={20} color="var(--color-info)" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-foreground mb-1">{t?.adminNotes}</p>
                          <p className="text-sm text-muted">{application?.adminNotes}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rejection Reason */}
                  {application?.rejectionReason && (
                    <div className="bg-danger/5 rounded-lg p-4 border border-danger/20">
                      <div className="flex items-start gap-3">
                        <Icon name="AlertCircle" size={20} color="var(--color-danger)" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-foreground mb-1">{t?.rejectionReason}</p>
                          <p className="text-sm text-muted">{application?.rejectionReason}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Business Information */}
                  <div className="bg-background rounded-lg p-6 border border-border">
                    <h4 className="text-base font-semibold text-foreground mb-4">{t?.businessInfo}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted mb-1">{t?.businessName}</p>
                        <p className="text-sm text-foreground">{application?.businessName}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted mb-1">{t?.contactPerson}</p>
                        <p className="text-sm text-foreground">{application?.contactPerson}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted mb-1">{t?.email}</p>
                        <p className="text-sm text-foreground">{application?.email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted mb-1">{t?.phone}</p>
                        <p className="text-sm text-foreground">{application?.phone}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted mb-1">{t?.country}</p>
                        <p className="text-sm text-foreground">
                          {application?.country?.[language === 'en' ? 'nameEn' : 'nameZh'] || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted mb-1">{t?.supplierType}</p>
                        <p className="text-sm text-foreground capitalize">
                          {application?.supplierType?.replace('_', ' ')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {!application?.adminNotes && !application?.rejectionReason && (
                    <div className="text-center py-12">
                      <Icon name="MessageSquare" size={48} color="var(--color-muted)" />
                      <p className="text-muted mt-4">{t?.noMessages}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">{t?.emailNotifications}</h3>
                    <div className="space-y-4">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={emailPreferences?.statusUpdates}
                          onChange={(e) =>
                            setEmailPreferences({ ...emailPreferences, statusUpdates: e?.target?.checked })
                          }
                          className="w-5 h-5 text-primary border-border rounded focus:ring-primary"
                        />
                        <div>
                          <p className="text-sm font-medium text-foreground">{t?.statusUpdates}</p>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={emailPreferences?.documentRequests}
                          onChange={(e) =>
                            setEmailPreferences({ ...emailPreferences, documentRequests: e?.target?.checked })
                          }
                          className="w-5 h-5 text-primary border-border rounded focus:ring-primary"
                        />
                        <div>
                          <p className="text-sm font-medium text-foreground">{t?.documentRequests}</p>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={emailPreferences?.adminMessages}
                          onChange={(e) =>
                            setEmailPreferences({ ...emailPreferences, adminMessages: e?.target?.checked })
                          }
                          className="w-5 h-5 text-primary border-border rounded focus:ring-primary"
                        />
                        <div>
                          <p className="text-sm font-medium text-foreground">{t?.adminMessages}</p>
                        </div>
                      </label>
                    </div>
                    <Button className="mt-6" onClick={handleSavePreferences}>
                      {t?.savePreferences}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SupplierApplicationStatus;